/**
 * The back-office menu. A group holds pages; a page's `scope` is what a user
 * needs to see it (none: everyone with back-office access), and `ownership`
 * 'all' means the `.mine` form of the scope is not enough. A group is shown
 * when at least one of its pages is.
 */
export const NAV_GROUPS = [
    { key: '/dashboard', label: 'Dashboard' },
    {
        key: 'catalog-management',
        label: 'Catalog Management',
        children: [
            { key: '/ingestion', label: 'Upload Pipeline', scope: 'upload-pipeline.read', bold: true },
            { key: '/extension', label: 'Chrome Extension', scope: 'upload-pipeline.write' },
            { key: '/catalog', label: 'Catalog', scope: '*' },
            { key: '/category', label: 'Categories', scope: '*' },
            { key: '/retailer', label: 'Retailers', scope: '*' },
            { key: '/feedback', label: 'Users Feedback', scope: '*' },
            { key: '/models-inspector', label: 'Models Inspector', scope: '*' },
        ],
    },
    {
        key: 'challenges',
        label: 'Challenges',
        children: [
            { key: '/challenges', label: 'Challenges', scope: '*' },
            { key: '/templates', label: 'Templates', scope: '*' },
        ],
    },
    {
        key: 'geo-seo',
        label: 'GEO / SEO',
        children: [
            { key: '/geo-exposure/queries', label: 'Queries', scope: 'geo-exposure.manage' },
            { key: '/geo-exposure', label: 'Answers', scope: 'geo-exposure.manage' },
            { key: '/geo-exposure/settings', label: 'Configuration', scope: 'geo-exposure.manage' },
        ],
    },
    {
        key: 'community',
        label: 'Community Management',
        children: [
            { key: '/campaigns', label: 'In-app Campaigns', scope: 'campaigns.manage' },
            { key: '/grant-credits', label: 'Grant Gems & Coins', scope: '*' },
            { key: '/notifications', label: 'Notifications', scope: '*' },
            { key: '/content-reports', label: 'Content Reports', scope: '*' },
            { key: '/avatar-items', label: 'Avatar Items', scope: '*' },
        ],
    },
    {
        key: 'management',
        label: 'Management',
        children: [
            { key: '/report', label: 'Catalog Report', scope: 'upload-pipeline.read', ownership: 'all' },
            { key: '/ingestion/costs', label: 'Pipeline Costs', scope: 'upload-pipeline.read' },
            { key: '/render-performance', label: 'Rendering Performance', scope: '*' },
            { key: '/experiments', label: 'Rendering Experiments', scope: '*' },
            { key: '/economy', label: 'Economy', scope: '*' },
            { key: '/users', label: 'Users', scope: 'admin-users.manage' },
        ],
    },
];

export const CONTENT_REPORTS_KEY = '/content-reports';

/** Every page (a menu entry with a path), in menu order. */
const NAV_PAGES = NAV_GROUPS.flatMap((entry) => entry.children ?? [entry]);

const canSee = (currentUserStore, page) => !page.scope || currentUserStore.can(page.scope, page.ownership);

/** The menu as this user sees it: groups without a visible page are dropped. */
export const visibleNavGroups = (currentUserStore) =>
    NAV_GROUPS.map((entry) => (entry.children ? { ...entry, children: entry.children.filter((page) => canSee(currentUserStore, page)) } : entry))
        .filter((entry) => !entry.children || entry.children.length > 0)
        .filter((entry) => entry.children || canSee(currentUserStore, entry));

export const visiblePages = (currentUserStore) => NAV_PAGES.filter((page) => canSee(currentUserStore, page));

/** The page a path belongs to: the longest menu key the path starts with (`/ingestion/costs` wins over `/ingestion`). */
export const pageForPath = (pathname) =>
    NAV_PAGES.filter((page) => pathname === page.key || pathname.startsWith(`${page.key}/`)).sort((a, b) => b.key.length - a.key.length)[0] ?? null;

export const groupOfPage = (pageKey) => NAV_GROUPS.find((entry) => entry.children?.some((page) => page.key === pageKey))?.key ?? null;
